import React, { useState, useContext } from 'react';
import axios from 'axios';
import { LoginContext } from '../../reactcontext/ReactContext';

const SupportTicketList = ({ tickets, loading, isAdmin, onUpdate }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { loginInfo } = useContext(LoginContext);
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState('');
  const currentUserId = loginInfo.userDetails?.id;

  const handleStatusUpdate = async (ticketId, newStatus) => {
    setUpdating(ticketId);
    setError('');
    try {
      await axios.put(
        `${apiUrl}/support/${ticketId}`,
        { status: newStatus },
        {
          headers: {
            loginToken: localStorage.getItem('loginToken'),
          },
        }
      );
      onUpdate();
    } catch (error) {
      console.error('Error updating ticket:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update ticket status';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setUpdating(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-gray-100 text-gray-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className='text-center py-8'>Loading tickets...</div>;
  }

  if (tickets.length === 0) {
    return (
      <div className='text-center py-8'>
        <p className='text-gray-500'>No support tickets found.</p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {error && (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded'>
          <strong>Error: </strong>{error}
        </div>
      )}
      <div className='bg-white rounded-lg shadow overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Ticket ID
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Title
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Priority
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Created By
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Created At
                </th>
                {isAdmin && (
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {tickets.map((ticket) => (
                <tr key={ticket.id} className='hover:bg-gray-50'>
                  <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                    #{ticket.id}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                    <div className='max-w-xs truncate' title={ticket.title}>
                      {ticket.title}
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                    <span>
                      {ticket.creator?.firstName} {ticket.creator?.lastName}
                      {ticket.createdBy === currentUserId && (
                        <span className='ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium'>You</span>
                      )}
                    </span>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </td>
                  {isAdmin && (
                    <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                      <select
                        value={ticket.status}
                        onChange={(e) => handleStatusUpdate(ticket.id, e.target.value)}
                        disabled={updating === ticket.id}
                        className='border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed'
                        title='Update ticket status'
                      >
                        <option value='open'>Open</option>
                        <option value='in_progress'>In Progress</option>
                        <option value='resolved'>Resolved</option>
                        <option value='closed'>Closed</option>
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketList;